import { createFileRoute, useRouter } from '@tanstack/react-router';
import { Plus, UserRound } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

import { AdminOrgPicker } from '@apps/web/components/admin/admin_org_picker';
import { ClientFormModal } from '@apps/web/components/admin/client_form_modal';
import {
    AdminBadge,
    AdminStatusDot,
} from '@apps/web/features/admin_design/admin_badge';
import {
    AdminButton,
    AdminSearchInput,
} from '@apps/web/features/admin_design/admin_controls';
import {
    AdminPage,
    AdminPageBody,
    AdminPageHeader,
} from '@apps/web/features/admin_design/admin_shell';
import { AdminTableSkeleton } from '@apps/web/features/admin_design/admin_skeleton';
import {
    AdminEmptyState,
    AdminErrorState,
} from '@apps/web/features/admin_design/admin_states';
import {
    AdminTable,
    AdminTableBody,
    AdminTableCell,
    AdminTableHeadCell,
    AdminTableHeader,
    AdminTableRow,
} from '@apps/web/features/admin_design/admin_table';
import { useDebounce } from '@apps/web/hooks/use_debounce';
import { getNavigableElementProps } from '@apps/web/lib/navigation_utils';
import {
    useAdminClients,
    useOrganizations,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type AdminClientListResponse } from '@libraries/typescript/api_client';

const clientsSearchSchema = z.object({
    organizationId: z.string().optional(),
    search: z.string().optional(),
});

export const Route = createFileRoute('/_app/admin/clients/')({
    head: () => ({
        meta: [
            {
                title: 'Clients | Phoebe Admin',
            },
        ],
    }),
    validateSearch: clientsSearchSchema,
    component: AdminClientsPage,
});

const CLIENT_TABLE_COLUMNS = [
    'Name',
    'Organization',
    'Organization Internal Name',
    'City',
    'Phone',
    'Mode',
    'Status',
] as const;

function AdminClientsPage() {
    const router = useRouter();
    const navigate = Route.useNavigate();
    const { organizationId, search } = Route.useSearch();
    const searchQuery = search ?? '';
    const [searchDraft, setSearchDraft] = useState(searchQuery);
    const debouncedSearch = useDebounce(searchDraft, 300);
    // Distinguishes user typing from URL-driven draft syncs so the
    // debounced write-back below can't replay a stale value over an
    // external navigation (e.g. browser back/forward).
    const searchDraftEditedRef = useRef(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    useEffect(() => {
        searchDraftEditedRef.current = false;
        setSearchDraft(searchQuery);
    }, [searchQuery]);

    useEffect(() => {
        if (!searchDraftEditedRef.current) {
            return;
        }
        if (debouncedSearch === searchQuery) {
            return;
        }

        void navigate({
            search: (current) => ({
                ...current,
                search: debouncedSearch || undefined,
            }),
            replace: true,
        });
    }, [debouncedSearch, navigate, searchQuery]);

    const {
        data: clientsResponse,
        isLoading,
        error,
        hasRequestFailed,
        mutate: mutateClients,
    } = useAdminClients({
        page: 1,
        per_page: 100,
        ...(organizationId && { organization_id: organizationId }),
        ...(searchQuery && { search: searchQuery }),
    }) as {
        data: AdminClientListResponse | undefined;
        isLoading: boolean;
        error: Error | undefined;
        hasRequestFailed: boolean;
        mutate: () => void;
    };

    const { data: organizations } = useOrganizations();
    const clients = clientsResponse?.clients ?? [];
    const hasLoadError =
        error !== undefined ||
        (hasRequestFailed && clientsResponse === undefined);
    const organizationInternalNamesById = useMemo(
        () =>
            new Map(
                (organizations ?? []).map((organization) => [
                    organization.id,
                    organization.internal_name,
                ]),
            ),
        [organizations],
    );
    const getClientNavigation = (clientId: string) => {
        const locationOptions = {
            to: '/admin/clients/$client-id',
            params: { 'client-id': clientId },
            // Embed the live input draft rather than the URL param so a
            // click within the debounce window still carries the filter
            // text visible in the search box.
            search: { organizationId, search: searchDraft || undefined },
        } as const;
        return {
            href: router.buildLocation(locationOptions).href,
            navigate: () => {
                void router.navigate(locationOptions);
            },
        };
    };

    return (
        <AdminPage>
            <AdminPageHeader
                title="Clients"
                meta={`${clientsResponse?.total ?? 0} ${
                    organizationId ? 'in organization' : 'total'
                }`}
                actions={
                    <AdminButton
                        onClick={() => setIsCreateModalOpen(true)}
                        variant="primary"
                    >
                        <Plus strokeWidth={1.75} />
                        Create
                    </AdminButton>
                }
            >
                <AdminSearchInput
                    placeholder="Search clients…"
                    containerClassName="max-w-md"
                    value={searchDraft}
                    onChange={(e) => {
                        searchDraftEditedRef.current = true;
                        setSearchDraft(e.target.value);
                    }}
                />
                <div className="flex items-center gap-2">
                    <span className="text-admin-sm text-admin-ink-muted font-medium">
                        Organization:
                    </span>
                    <AdminOrgPicker
                        triggerClassName="rounded-admin-sm border-admin-border text-admin-base h-8 w-[250px]"
                        value={organizationId}
                        onValueChange={(id) => {
                            void router.navigate({
                                to: '/admin/clients',
                                search: (prev) => ({
                                    ...prev,
                                    organizationId: id,
                                }),
                            });
                        }}
                    />
                </div>
            </AdminPageHeader>
            <AdminPageBody>
                {isLoading ? (
                    <AdminTableSkeleton columns={CLIENT_TABLE_COLUMNS} />
                ) : hasLoadError ? (
                    <AdminErrorState
                        title="Failed to load clients"
                        description="Please try again."
                        onRetry={() => {
                            mutateClients();
                        }}
                    />
                ) : clients?.length === 0 ? (
                    <AdminEmptyState
                        icon={UserRound}
                        title={
                            searchQuery ? 'No clients found' : 'No clients yet'
                        }
                        description={
                            searchQuery
                                ? 'Try a different name or clear the search.'
                                : 'Clients appear here once an organization syncs.'
                        }
                    />
                ) : (
                    <AdminTable>
                        <AdminTableHeader>
                            <tr>
                                {CLIENT_TABLE_COLUMNS.map((column) => (
                                    <AdminTableHeadCell key={column}>
                                        {column}
                                    </AdminTableHeadCell>
                                ))}
                            </tr>
                        </AdminTableHeader>
                        <AdminTableBody>
                            {clients.map((client) => (
                                <AdminTableRow
                                    key={client.id}
                                    className="cursor-pointer"
                                    {...getNavigableElementProps(
                                        getClientNavigation(client.id),
                                    )}
                                >
                                    <AdminTableCell className="text-admin-ink font-medium">
                                        {client.first_name} {client.last_name}
                                        {client.preferred_name && (
                                            <span className="text-admin-ink-muted ml-1 font-normal">
                                                ({client.preferred_name})
                                            </span>
                                        )}
                                    </AdminTableCell>
                                    <AdminTableCell>
                                        {client.organization_name}
                                    </AdminTableCell>
                                    <AdminTableCell>
                                        {organizationInternalNamesById.get(
                                            client.organization_id,
                                        ) ?? (
                                            <span className="text-admin-ink-faint">
                                                —
                                            </span>
                                        )}
                                    </AdminTableCell>
                                    <AdminTableCell>
                                        {client.city || (
                                            <span className="text-admin-ink-faint">
                                                —
                                            </span>
                                        )}
                                    </AdminTableCell>
                                    <AdminTableCell>
                                        {client.phone_number || (
                                            <span className="text-admin-ink-faint">
                                                —
                                            </span>
                                        )}
                                    </AdminTableCell>
                                    <AdminTableCell>
                                        {client.mode === 'sandbox' ? (
                                            <AdminBadge tone="outline">
                                                Sandbox
                                            </AdminBadge>
                                        ) : (
                                            <AdminBadge
                                                variant="solid"
                                                tone="ink-primary"
                                            >
                                                Live
                                            </AdminBadge>
                                        )}
                                    </AdminTableCell>
                                    <AdminTableCell>
                                        <AdminStatusDot
                                            tone={
                                                client.is_active
                                                    ? 'ok'
                                                    : 'neutral'
                                            }
                                            label={
                                                client.is_active
                                                    ? 'Active'
                                                    : 'Inactive'
                                            }
                                        />
                                    </AdminTableCell>
                                </AdminTableRow>
                            ))}
                        </AdminTableBody>
                    </AdminTable>
                )}
            </AdminPageBody>
            <ClientFormModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                mode="create"
                onSaveSuccess={() => mutateClients()}
            />
        </AdminPage>
    );
}
